import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import MainLayout from '../../components/layout/MainLayout';
import { mlApi, Anomaly, ForecastData, Incident } from '../../services/api/ml.api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';

export default function MLInsightsPage() {
  const { t } = useTranslation(['ml', 'common']);
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [forecastData, setForecastData] = useState<ForecastData[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [correlationData, setCorrelationData] = useState<{ metric: string; value: number; color: string }[]>([]);

  // Stats
  const [stats, setStats] = useState({
    totalAnomalies: 0,
    criticalAnomalies: 0,
    activeIncidents: 0,
    avgConfidence: 0
  });

  useEffect(() => {
    fetchData();
  }, [selectedAppId]); // Reload when app selection changes

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [anomaliesData, forecastsData, incidentsData, correlationsData] = await Promise.all([
        mlApi.getAnomalies(50, selectedAppId),
        mlApi.getForecasts('cpu_usage', 24, selectedAppId),
        mlApi.getIncidents(undefined, selectedAppId),
        mlApi.getMetricCorrelations(selectedAppId)
      ]);

      setAnomalies(anomaliesData);
      setForecastData(forecastsData);
      setIncidents(incidentsData);
      setCorrelationData(correlationsData);

      // Calculate stats
      const critical = anomaliesData.filter(a => a.severity === 'critical' || a.severity === 'high').length;
      const active = incidentsData.filter(i => i.status === 'open' || i.status === 'investigating').length;

      setStats({
        totalAnomalies: anomaliesData.length,
        criticalAnomalies: critical,
        activeIncidents: active,
        avgConfidence: 87.5 // This could be calculated from anomaly scores
      });
    } catch (err: any) {
      setError(err.message || t('ml:errorLoading'));
      console.error('ML insights fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('ml:loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('common:retry')}
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('ml:title')}
            <span className="ml-3 inline-flex items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-1 text-sm font-semibold text-white">
              {t('ml:aiPowered')}
            </span>
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('ml:subtitle')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Anomalies */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('ml:anomalies.title')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {stats.totalAnomalies}
                </p>
              </div>
              <div className="rounded-full bg-indigo-100 p-3 dark:bg-indigo-900/20">
                <svg className="h-8 w-8 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <Link to="/ml/anomalies" className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">
                {t('ml:viewAll')} →
              </Link>
            </div>
          </div>

          {/* Critical Anomalies */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('ml:anomalies.critical')}
                </p>
                <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
                  {stats.criticalAnomalies}
                </p>
              </div>
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
                <svg className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {t('ml:anomalies.high')} & {t('ml:anomalies.critical')} {t('ml:anomalies.severity')}
            </p>
          </div>

          {/* Active Incidents */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('ml:incidents.title')}
                </p>
                <p className="mt-2 text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.activeIncidents}
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3 dark:bg-orange-900/20">
                <svg className="h-8 w-8 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mt-4">
              <Link to="/ml/root-cause" className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400">
                {t('ml:incidents.investigate')} →
              </Link>
            </div>
          </div>

          {/* ML Confidence */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  ML {t('ml:rootCause.confidence')}
                </p>
                <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
                  {stats.avgConfidence}%
                </p>
              </div>
              <div className="rounded-full bg-green-100 p-3 dark:bg-green-900/20">
                <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {t('ml:anomalies.avgScore')}
            </p>
          </div>
        </div>

        {/* Metric Correlations Chart */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('ml:metricCorrelations.title')}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('ml:metricCorrelations.description')}
              </p>
            </div>
            <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
              {t('ml:metricCorrelations.aiAnalysis')}
            </span>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={correlationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey="metric"
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#9CA3AF"
                style={{ fontSize: '12px' }}
                label={{ value: t('ml:metricCorrelations.correlationPercent'), angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB',
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {correlationData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
            {correlationData.map((item, idx) => (
              <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.metric}
                  </span>
                  <span
                    className={`text-lg font-bold ${
                      item.value >= 70 ? 'text-red-600 dark:text-red-400' :
                      item.value >= 50 ? 'text-orange-600 dark:text-orange-400' :
                      'text-green-600 dark:text-green-400'
                    }`}
                  >
                    {item.value}%
                  </span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${item.value}%`,
                      backgroundColor: item.color
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link
            to="/ml/anomalies"
            className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-indigo-500 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-indigo-400"
          >
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/20">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('ml:quickLinks.anomalyDetection.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t('ml:quickLinks.anomalyDetection.description')}
                </p>
              </div>
              <svg className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="mt-4 text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              {anomalies.length}
            </div>
          </Link>

          <Link
            to="/ml/forecasts"
            className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-indigo-500 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-indigo-400"
          >
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/20">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('ml:quickLinks.forecasts.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t('ml:quickLinks.forecasts.description')}
                </p>
              </div>
              <svg className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="mt-4 text-2xl font-bold text-purple-600 dark:text-purple-400">
              {forecastData.length}
            </div>
          </Link>

          <Link
            to="/ml/root-cause"
            className="group rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-indigo-500 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-indigo-400"
          >
            <div className="flex items-center space-x-3">
              <div className="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/20">
                <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('ml:quickLinks.rootCause.title')}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t('ml:quickLinks.rootCause.description')}
                </p>
              </div>
              <svg className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="mt-4 text-2xl font-bold text-orange-600 dark:text-orange-400">
              {incidents.length}
            </div>
          </Link>
        </div>

        {/* Recent Anomalies */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('ml:recentAnomalies.title')}
            </h2>
            <Link
              to="/ml/anomalies"
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
            >
              {t('ml:viewAll')} →
            </Link>
          </div>
          <div className="space-y-4">
            {anomalies.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-800 dark:bg-gray-900">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  {t('ml:anomalies.noAnomalies')}
                </p>
              </div>
            ) : (
              anomalies.slice(0, 5).map((anomaly) => (
                <div
                  key={anomaly.id}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span
                          className={`inline-flex h-3 w-3 rounded-full ${
                            anomaly.severity === 'critical' || anomaly.severity === 'high'
                              ? 'bg-red-500 animate-pulse'
                              : 'bg-yellow-500'
                          }`}
                        ></span>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {anomaly.metric_name || anomaly.anomaly_type}
                        </h3>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            anomaly.severity === 'critical' || anomaly.severity === 'high'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                          }`}
                        >
                          {anomaly.severity.toUpperCase()}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {t('ml:recentAnomalies.detected')}: {new Date(anomaly.detected_at).toLocaleString()}
                      </p>
                      {anomaly.actual_value !== undefined && anomaly.actual_value !== null &&
                       anomaly.expected_value !== undefined && anomaly.expected_value !== null && (
                        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('ml:anomalies.observedValue')}
                            </p>
                            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                              {anomaly.actual_value.toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('ml:anomalies.expectedValue')}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                              {anomaly.expected_value.toFixed(2)}
                            </p>
                          </div>
                          {anomaly.deviation_percent !== undefined && anomaly.deviation_percent !== null && (
                            <div>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('ml:anomalies.deviation')}
                              </p>
                              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                {anomaly.deviation_percent.toFixed(1)}%
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Forecast Chart */}
        {forecastData.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('ml:forecast.title')}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t('ml:forecasts.actualVsPredicted')}
                </p>
              </div>
              <Link
                to="/ml/forecasts"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
              >
                {t('ml:viewAllForecasts')} →
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={forecastData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                <XAxis
                  dataKey="time"
                  stroke="#9CA3AF"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#10B981"
                  strokeWidth={2}
                  name={t('ml:forecast.actual')}
                  dot={{ fill: '#10B981', r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="predicted"
                  stroke="#6366F1"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name={t('ml:forecast.predicted')}
                  dot={{ fill: '#6366F1', r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="upperBound"
                  stroke="#9CA3AF"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  name={t('ml:forecast.upperBound')}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="lowerBound"
                  stroke="#9CA3AF"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  name={t('ml:forecast.lowerBound')}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
